import { formDecode } from './form-decode';
import { decodeHtmlEntities } from './html-entity-decode';

/**
 * The ID of the construct that made a value unsafe (C2):
 * - `element`: a dangerous element opens or closes, such as `<script>` or `</svg`;
 * - `declaration`: a declaration or processing instruction starts, `<!` or `<?`;
 * - `attribute`: a dangerous attribute such as `onerror=` follows a tag opener;
 * - `script-url`: a script URL such as `javascript:` follows a tag opener.
 */
export type UnsafeMarkupRule = 'attribute' | 'declaration' | 'element' | 'script-url';

interface AsciiLiteral {
  readonly lower: string;
  readonly upper: string;
}

/**
 * Pairs an ASCII literal with its uppercase form, so a scan compares ASCII case-insensitively without allocating.
 *
 * @param lower The literal in lowercase ASCII: a name, a scheme or a URL prefix.
 * @returns The literal in both cases.
 */
function toAsciiLiteral(lower: string): AsciiLiteral {
  return { lower, upper: lower.toUpperCase() };
}

/**
 * Pairs each ASCII literal of a list with its uppercase form.
 *
 * @param lowers The literals in lowercase ASCII.
 * @returns The literals in both cases.
 */
function toAsciiLiterals(lowers: readonly string[]): readonly AsciiLiteral[] {
  return lowers.map((lower) => toAsciiLiteral(lower));
}

const DANGEROUS_ELEMENT_NAMES = [
  'script',
  'iframe',
  'frame',
  'frameset',
  'object',
  'embed',
  'applet',
  'svg',
  'math',
  'style',
  'link',
  'meta',
  'base',
  'form',
  'template',
  'noscript',
  'portal',
  'xml',
] as const;

const DANGEROUS_ELEMENTS = toAsciiLiterals(DANGEROUS_ELEMENT_NAMES);
const EVENT_HANDLER_PREFIX = toAsciiLiteral('on');
const DANGEROUS_ATTRIBUTES = toAsciiLiterals(['style', 'srcdoc', 'formaction', 'xlink:href']);
const SCRIPT_URL_SCHEMES = toAsciiLiterals(['javascript', 'vbscript', 'livescript']);
const HTML_DATA_URL = toAsciiLiteral('data:text/html');
const LESS_THAN = '<';
const ATTRIBUTE_BOUNDARIES = '/"\'';
const URL_IGNORED_CHARACTERS = /[\t\n\r]/g;
const NORMALIZATION_FORM = 'NFKD';

/** The decodes between two stages: two entity passes, then the one extra URL pass (`s1`, `s2`, `s3`). */
const STAGE_DECODERS: readonly ((text: string) => string)[] = [decodeHtmlEntities, decodeHtmlEntities, formDecode];

/**
 * Decides whether a decoded query key or value holds a markup construct a browser could run.
 *
 * Every check is a forward scan with no unbounded regex repeat, so the time stays linear in the length of the value.
 * The value is checked at four stages, each normalized with NFKD: itself, after one and after two entity passes,
 * and after one more URL decode. A construct visible at any stage makes it unsafe; a third layer stays encoded.
 *
 * @param value The query key or value, after the first decode.
 * @returns The ID of the first construct found, or `null` when the value is safe.
 */
export function isUnsafeMarkup(value: string): UnsafeMarkupRule | null {
  for (const stage of stageTexts(value)) {
    const rule = firstConstruct(stage);
    if (rule !== null) {
      return rule;
    }
  }
  return null;
}

/**
 * Reads forward from the end of an attribute name: optional spaces, then `=`.
 *
 * @param text The text being scanned.
 * @param index The position right after the attribute name.
 * @returns Whether an assignment follows.
 */
function assignsAt(text: string, index: number): boolean {
  return text[skipSpaces(text, index)] === '=';
}

/**
 * Tells whether a dangerous attribute name starts at a position: `on` plus one or more letters, or a name of the
 * list, and in both cases optional spaces, then `=`.
 *
 * @param text The text being scanned.
 * @param index The position right after the character that introduces the attribute.
 * @returns Whether a dangerous attribute is assigned there.
 */
function dangerousAttributeStartsAt(text: string, index: number): boolean {
  if (matchesLiteralAt(text, index, EVENT_HANDLER_PREFIX)) {
    const nameStart = index + EVENT_HANDLER_PREFIX.lower.length;
    const nameEnd = skipLetters(text, nameStart);
    if (nameEnd > nameStart && assignsAt(text, nameEnd)) {
      return true;
    }
  }

  return DANGEROUS_ATTRIBUTES.some(
    (attribute) => matchesLiteralAt(text, index, attribute) && assignsAt(text, index + attribute.lower.length),
  );
}

/**
 * Tells whether a dangerous element name ends where the name's last letter was matched.
 *
 * @param text The text being scanned.
 * @param index The position right after the matched name.
 * @returns Whether the name stands alone: the text ends, or a space, `/`, `>` or `-` follows.
 */
function endsNameAt(text: string, index: number): boolean {
  if (index >= text.length) {
    return true;
  }
  const next = text[index];
  return next === '/' || next === '>' || next === '-' || isAsciiSpace(next);
}

/**
 * Finds the first `<` that a check passes at.
 *
 * @param text The text to scan.
 * @param test Reads forward from the position of one `<`.
 * @returns The position of that `<`, or -1 when no `<` passes.
 */
function findLessThan(text: string, test: (lessThan: number) => boolean): number {
  for (let lessThan = text.indexOf(LESS_THAN); lessThan !== -1; lessThan = text.indexOf(LESS_THAN, lessThan + 1)) {
    if (test(lessThan)) {
      return lessThan;
    }
  }
  return -1;
}

/**
 * Finds the first construct in a text, checking constructs in rule order.
 *
 * @param text The text to scan.
 * @returns The rule ID, or `null` when the text holds no construct.
 */
function firstConstruct(text: string): UnsafeMarkupRule | null {
  if (hasDangerousElement(text)) {
    return 'element';
  }
  if (hasDeclaration(text)) {
    return 'declaration';
  }

  const tagOpener = indexOfTagOpener(text);
  if (tagOpener === -1) {
    return null;
  }
  if (hasDangerousAttribute(text, tagOpener)) {
    return 'attribute';
  }
  return hasScriptUrl(text, tagOpener) ? 'script-url' : null;
}

/**
 * Checks everything after the first tag opener for a dangerous attribute: a space, `/`, `"` or `'`, then the
 * attribute name and its `=`. No tag end is searched, so quoting cannot move an attribute out of reach.
 *
 * @param text The text to scan.
 * @param tagOpener The position of the first tag opener.
 * @returns Whether a dangerous attribute is assigned after it.
 */
function hasDangerousAttribute(text: string, tagOpener: number): boolean {
  for (let index = tagOpener + 1; index < text.length; index += 1) {
    if (isAttributeBoundary(text[index]) && dangerousAttributeStartsAt(text, index + 1)) {
      return true;
    }
  }
  return false;
}

/**
 * Checks every `<` of the text for a dangerous element.
 *
 * @param text The text to scan.
 * @returns Whether a dangerous element opens or closes anywhere in the text.
 */
function hasDangerousElement(text: string): boolean {
  return someLessThan(text, (lessThan) => hasDangerousElementAt(text, lessThan));
}

/**
 * Reads forward from one `<`: optional spaces, an optional `/`, optional spaces, then a dangerous element name.
 *
 * @param text The text being scanned.
 * @param lessThan The position of the `<`.
 * @returns Whether a dangerous element name follows that `<`.
 */
function hasDangerousElementAt(text: string, lessThan: number): boolean {
  let index = skipSpaces(text, lessThan + 1);
  if (text[index] === '/') {
    index = skipSpaces(text, index + 1);
  }

  return DANGEROUS_ELEMENTS.some(
    (element) => matchesLiteralAt(text, index, element) && endsNameAt(text, index + element.lower.length),
  );
}

/**
 * Checks every `<` of the text for a declaration or processing instruction (`<!`, `<?`).
 *
 * @param text The text to scan.
 * @returns Whether any `<` is directly followed by `!` or `?`.
 */
function hasDeclaration(text: string): boolean {
  return someLessThan(text, (lessThan) => {
    const next = text[lessThan + 1];
    return next === '!' || next === '?';
  });
}

/**
 * Checks everything after the first tag opener for a script URL, read as a browser's URL parser reads it: with every
 * tab, line feed and carriage return removed.
 *
 * @param text The text to scan.
 * @param tagOpener The position of the first tag opener.
 * @returns Whether a script scheme and its `:`, or `data:text/html`, follows the tag opener.
 */
function hasScriptUrl(text: string, tagOpener: number): boolean {
  const urlText = stripUrlIgnoredCharacters(text.slice(tagOpener));

  for (let index = 0; index < urlText.length; index += 1) {
    const isScriptScheme = SCRIPT_URL_SCHEMES.some(
      (scheme) =>
        matchesLiteralAt(urlText, index, scheme) && urlText[skipSpaces(urlText, index + scheme.lower.length)] === ':',
    );
    if (isScriptScheme || matchesLiteralAt(urlText, index, HTML_DATA_URL)) {
      return true;
    }
  }
  return false;
}

/**
 * Finds the first tag opener: a `<` directly followed by a letter.
 *
 * @param text The text to scan.
 * @returns The position of its `<`, or -1 when the text holds none.
 */
function indexOfTagOpener(text: string): number {
  return findLessThan(text, (lessThan) => isAsciiLetter(text[lessThan + 1]));
}

/**
 * Tells the letters of the rule: ASCII `A`-`Z` and `a`-`z`.
 *
 * @param char One character, or `undefined` past the end of the text.
 * @returns Whether it is an ASCII letter; never a Unicode `\w`, whose set differs between JS and .NET.
 */
function isAsciiLetter(char: string | undefined): boolean {
  return char !== undefined && ((char >= 'a' && char <= 'z') || (char >= 'A' && char <= 'Z'));
}

/**
 * Tells the spaces of the rule: tab, line feed, vertical tab, form feed, carriage return and space.
 *
 * @param char One character.
 * @returns Whether it is one of those spaces; never a Unicode `\s`, whose set differs between JS and .NET.
 */
function isAsciiSpace(char: string): boolean {
  return char === ' ' || char === '\t' || char === '\n' || char === '\v' || char === '\f' || char === '\r';
}

/**
 * Tells the characters that introduce an attribute name: a space, `/`, `"` or `'`.
 *
 * @param char One character.
 * @returns Whether an attribute name may follow it.
 */
function isAttributeBoundary(char: string): boolean {
  return isAsciiSpace(char) || ATTRIBUTE_BOUNDARIES.includes(char);
}

/**
 * Compares a literal with the text, ASCII case-insensitively.
 *
 * @param text The text being scanned.
 * @param index Where the literal should start.
 * @param literal The literal in lowercase and uppercase ASCII.
 * @returns Whether the text holds the literal at `index`.
 */
function matchesLiteralAt(text: string, index: number, literal: AsciiLiteral): boolean {
  if (index + literal.lower.length > text.length) {
    return false;
  }

  for (let offset = 0; offset < literal.lower.length; offset += 1) {
    const char = text[index + offset];
    if (char !== literal.lower[offset] && char !== literal.upper[offset]) {
      return false;
    }
  }
  return true;
}

/**
 * Skips the letters of the rule.
 *
 * @param text The text being scanned.
 * @param from Where to start.
 * @returns The position of the first character that is no letter, or the text length.
 */
function skipLetters(text: string, from: number): number {
  let index = from;
  while (index < text.length && isAsciiLetter(text[index])) {
    index += 1;
  }
  return index;
}

/**
 * Skips the spaces of the rule.
 *
 * @param text The text being scanned.
 * @param from Where to start.
 * @returns The position of the first character that is no space, or the text length.
 */
function skipSpaces(text: string, from: number): number {
  let index = from;
  while (index < text.length && isAsciiSpace(text[index])) {
    index += 1;
  }
  return index;
}

/**
 * Runs a check from every `<` of the text, left to right, and stops at the first one that passes.
 *
 * @param text The text to scan.
 * @param test Reads forward from the position of one `<`.
 * @returns Whether the check passed at any `<`.
 */
function someLessThan(text: string, test: (lessThan: number) => boolean): boolean {
  return findLessThan(text, test) !== -1;
}

/**
 * Produces the text of each stage in order, decoding the next one only when the caller asks for it.
 *
 * @param value The query key or value, after the first decode.
 * @yields {string} `s0` to `s3`, each normalized.
 */
function* stageTexts(value: string): Generator<string> {
  let stage = value.normalize(NORMALIZATION_FORM);
  yield stage;

  for (const decode of STAGE_DECODERS) {
    stage = decode(stage).normalize(NORMALIZATION_FORM);
    yield stage;
  }
}

/**
 * Removes the tabs, line feeds and carriage returns a browser's URL parser removes.
 *
 * @param text The text to read as a URL.
 * @returns The text without them.
 */
function stripUrlIgnoredCharacters(text: string): string {
  return text.replaceAll(URL_IGNORED_CHARACTERS, '');
}
